import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error("Missing Supabase environment variables");
}

export const SUPABASE_URL = supabaseUrl as string;

const LOCK_TIMEOUT_MS = 3000;

const lockWithFallback = async <R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>
): Promise<R> => {
  if (typeof navigator === "undefined" || !navigator.locks) {
    return await fn();
  }

  const effectiveTimeout =
    acquireTimeout > 0 ? Math.min(acquireTimeout, LOCK_TIMEOUT_MS) : acquireTimeout;

  const controller = new AbortController();
  const timer =
    effectiveTimeout > 0
      ? setTimeout(() => controller.abort(), effectiveTimeout)
      : null;

  try {
    return await navigator.locks.request(
      name,
      { mode: "exclusive", signal: controller.signal },
      async () => {
        if (timer) clearTimeout(timer);
        return await fn();
      }
    );
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      // Fall through unguarded. Single-user PWA — a stale auth state from a
      // hung lock is worse than a benign cross-tab refresh race the server
      // tolerates.
      return await fn();
    }
    throw err;
  } finally {
    if (timer) clearTimeout(timer);
  }
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    lock: lockWithFallback,
  },
});
